<template>
  <div
    class="lisaui:flex lisaui:px-2 lisaui:border lisaui:rounded-md lisaui:items-center lisaui:relative lisaui:bg-background"
    style="gap: 5px"
  >
    <span>
      <i
        v-if="!filtersAreEmpty"
        class="fas mr-2 fa-sync lisaui:cursor-pointer"
        @click="refreshFilter"
      />
      <span
        v-if="eventFilters.length === 0 && actionFilters.length === 0"
        style="line-height: 2.45em"
      >
        {{ t("components.eventActionBoard.filter.FilterView.noFilterAvailable") }}
      </span>
    </span>
    <div class="lisaui:flex lisaui:flex-wrap flex-grow lisaui:items-center filter-box py-05">
      <component
        :is="badgeForFilter(filter)"
        v-for="(filter, index) in eventFilters"
        :key="'event' + index"
        class="eventBadge"
        :types="eventTypes"
        :filter="filter"
        @click="selectFilter(index, FilterEntity.EVENT)"
        @remove="removeFilter(index, FilterEntity.EVENT)"
      />

      <component
        :is="badgeForFilter(filter)"
        v-for="(filter, index) in actionFilters"
        :key="'action' + index"
        class="actionBadge"
        :types="actionTypes"
        :filter="filter"
        @click="selectFilter(index, FilterEntity.ACTION)"
        @remove="removeFilter(index, FilterEntity.ACTION)"
      />
    </div>

    <div class="lisaui:cursor-pointer text-nowrap lisaui:flex lisaui:items-center">
      <SavedFilterManager :board-id="boardId" :filters="serializedFilters" />
      <i class="fa fa-times" @click="removeAllFilters" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType } from "vue";
import {
  type ActionFilter,
  type EventFilter,
  Filter,
  FilterEntity,
  type FilterEventActionTypes,
} from "./FilterTypes";
import FilterSerializer from "./FilterSerializer";
import SavedFilterManager from "./SavedFilterManager.vue";

const props = defineProps({
  eventFilters: {
    required: true,
    type: Array as PropType<Array<EventFilter>>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Array<ActionFilter>>,
  },
  eventTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits(["filterSelected", "filterRemoved", "allFiltersRemoved", "refreshFilter"]);

const selectFilter = (index: number, type: FilterEntity) => {
  emit("filterSelected", { type, index });
};
const removeFilter = (index: number, type: FilterEntity) => {
  emit("filterRemoved", { type, index });
};
const removeAllFilters = () => {
  emit("allFiltersRemoved");
};
const refreshFilter = () => {
  emit("refreshFilter");
};
const badgeForFilter = (filter: Filter) => {
  const filterClass = filter.constructor as typeof Filter;
  return filterClass.Badge;
};
const filtersAreEmpty = computed(
  () => props.eventFilters.length === 0 && props.actionFilters.length === 0,
);
const serializedFilters = computed(() =>
  FilterSerializer.serialize({
    eventFilters: props.eventFilters,
    actionFilters: props.actionFilters,
  }),
);

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.py-05 {
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
}

.eventBadge {
  background-color: #b1d2b1 !important;
  padding-top: 0;
  padding-bottom: 0;
}

.actionBadge {
  background-color: #fedcba !important;
  padding-top: 0;
  padding-bottom: 0;
}

.filter-box {
  line-height: 1.8em;
  flex-grow: 99;
  gap: 2px;
}
</style>
